import type { VisualizationFontSize, VisualizationSpec, VisualizationStyle } from '@aibi/contracts';
import type { CSSProperties } from 'react';
import { resolveChartTheme, type ChartTheme } from './echarts/chart-theme';

const FONT_SCALE: Readonly<Record<VisualizationFontSize, number>> = {
  sm: 0.875,
  md: 1,
  lg: 1.15,
  xl: 1.35,
};

const KPI_VALUE_CLASS: Readonly<Record<VisualizationFontSize, string>> = {
  sm: 'text-2xl',
  md: 'text-3xl',
  lg: 'text-4xl',
  xl: 'text-5xl',
};

const TABLE_TEXT_CLASS: Readonly<Record<VisualizationFontSize, string>> = {
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-sm',
  xl: 'text-base',
};

export const fontScaleOf = (style: VisualizationStyle): number => FONT_SCALE[style.fontSize];

export const kpiValueClassOf = (style: VisualizationStyle): string => KPI_VALUE_CLASS[style.fontSize];

export const tableTextClassOf = (style: VisualizationStyle): string =>
  TABLE_TEXT_CLASS[style.fontSize];

/** Paleta del tema activo (tokens CSS), para previsualizar en el panel de propiedades. */
export const themePaletteOf = (element?: Element): readonly string[] =>
  resolveChartTheme(element).palette;

/** Variables CSS aplicadas al contenedor de la visualizacion. */
export function visualizationSurfaceStyle(spec: VisualizationSpec): CSSProperties {
  const { style } = spec;
  const css: CSSProperties & Record<string, string | number> = {
    '--viz-font-scale': String(fontScaleOf(style)),
  };
  if (style.foreground !== undefined) {
    css.color = style.foreground;
    css['--viz-foreground'] = style.foreground;
  }
  if (style.background !== undefined) {
    css.backgroundColor = style.background;
    css['--viz-background'] = style.background;
  }
  return css;
}

/** Combina el tema CSS de la app con los colores propios de la visualizacion. */
export function themeWithStyle(theme: ChartTheme, style: VisualizationStyle): ChartTheme {
  return {
    ...theme,
    text: style.foreground ?? theme.text,
    mutedText: style.foreground ?? theme.mutedText,
    surface: style.background ?? theme.surface,
    palette: style.palette.length > 0 ? style.palette : theme.palette,
  };
}
